import React from 'react';
import { inject, observer } from 'mobx-react';
import './sessionListPane.module.scss';
import { CalendarView } from '../../molecules/calendar/CalendarView';
import { DateGroup } from '../../molecules/reservable/DateGroup';
import { ScheduleListItem } from '../../molecules/reservable/ScheduleListItem';
import { groupByDate, ReservableType } from '../../../../commonv3/types/reservable';
import { BackButton } from '../../../../commonv3/components/atoms/buttons/BackButton';
import moment from 'moment-timezone';
import { Button } from '../../../../commonv3/components/atoms/buttons/Button';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { V3Routes } from '../../../routes';
import { isFullSession, SessionIndexView, SessionType } from '../../../types/sessions';
import { HomePageState } from '../../../containers/home/states/HomePageState';
import { parseQueryParams } from '../../../types/location';
import { getLocationSelector, getOnlineSelector, LocationSelector } from '../../molecules/forms/LocationSelector';
import { observable, makeObservable } from 'mobx';
import { SessionWaitlistState } from './states/SessionWaitlistState';
import { LoaderRow } from '../../atoms/layout/LoaderRow';
import { SessionWaitlistDescription } from '../../molecules/session_waitlists/SessionWaitlistDescription';
import { MembershipPlanGroupKey } from '../../../types/membershipPlans';
import { CheckBoxCardNoBorder } from '../../atoms/select/CheckBoxCardNoBorder';
import { CheckBoxSelectInput } from '../../molecules/forms/CheckBoxSelectInput';
import { RoomType } from './states/SessionListState';
import { SelectInput } from '../../../../commonv3/components/atoms/forms/primitive/SelectInput';
import { MokumokuPageState } from '../../../containers/mokumoku/states/MokumokuPageState';
import MokumokuGuideBanner from './images/mokumoku_guide_banner.png';

type Props = {
  homeState: HomePageState | MokumokuPageState;
};

type WailtlistToggleButtonProps = {
  entry: SessionIndexView;
  reserved: boolean;
  sessionWaitlistState: SessionWaitlistState;
};

export const WaitlistToggleButton = observer(
  ({ entry, reserved, sessionWaitlistState }: WailtlistToggleButtonProps) => {
    const { submitting, sessionWaitlists, createSessionWaitingList, cancelSessionWaitingList } = sessionWaitlistState;
    if (submitting) {
      return <LoaderRow />;
    }
    if (!sessionWaitlistState.canShowWaitlist(entry, reserved)) {
      return <div />;
    }

    const sessionWaitlist = sessionWaitlists.getOrElse([]).find((waitlist) => waitlist.session_id === entry.id);
    return sessionWaitlist ? (
      <div styleName="cancel-waitlist" onClick={() => cancelSessionWaitingList(sessionWaitlist.id)}>
        <span id="destroy-waitlist" styleName="waitlist">
          空き枠通知をOFFにする
        </span>
      </div>
    ) : (
      <div styleName="create-waitlist" onClick={() => createSessionWaitingList(entry.id)}>
        <span id="create-waitlist" styleName="waitlist">
          空き枠通知をONにする
        </span>
      </div>
    );
  }
);

export const MokumokuSessionListPane = inject('homeState')(
  observer(
    class MokumokuSessionListPane extends React.Component<Props> {
      sessionWaitlistState = new SessionWaitlistState();

      constructor(props: Props) {
        super(props);

        makeObservable(this, {
          sessionWaitlistState: observable,
        });
      }
      async componentDidMount() {
        const { sessionList } = this.props.homeState;
        const { homeLocation } = GlobalStatesV3.currentUser;
        await homeLocation.reload();
        const { isHomeLocationSet, isCurrentLocationOnline, locationId } = homeLocation;
        sessionList.setSessionType(SessionType.MOKUMOKU);
        const membershipPlanGroupKey = parseQueryParams(GlobalStatesV3.routing.location).key as MembershipPlanGroupKey;
        await Promise.all([
          this.sessionWaitlistState.reloadSessionWaitlists(),
          this.props.homeState.reloadLocations(),
          this.props.homeState.reloadSessionTemplates(sessionList.sessionType, membershipPlanGroupKey),
        ]);
        sessionList.setSelectedLocationSelectors(
          isHomeLocationSet && !isCurrentLocationOnline
            ? [getLocationSelector(locationId), getOnlineSelector()]
            : [getOnlineSelector()]
        );
      }

      // もくもく会はlikes用とmulticreator用があるので絞り込む
      filterSessionsByKey(sessions: SessionIndexView[], key: MembershipPlanGroupKey): SessionIndexView[] {
        return sessions.filter((session) => session.membership_plan_group_keys.includes(key));
      }

      render() {
        const { locations, reservationList, sessionTemplates, sessionList } = this.props.homeState;
        const {
          sessionsOfType,
          filteredSessions,
          selectedOnlyOpen,
          selectedLocationSelectors,
          selectedRoomTypes,
          setSelectedLocationSelectors,
          selectedMultipleDates,
          selectedSessionTemplateId,
          setSelectedDate,
          setSelectedMonth,
          setSelectedOnlyOpen,
          setSelectedRoomTypes,
          setSelectedSessionTemplateId,
          sessionListLoading,
        } = sessionList;

        const membershipPlanGroupKey = parseQueryParams(GlobalStatesV3.routing.location).key as MembershipPlanGroupKey;
        // 検索条件で絞り込まれたもくもく会
        const filteredMokumokuList = this.filterSessionsByKey(filteredSessions, membershipPlanGroupKey);
        const groups = groupByDate(filteredMokumokuList, (r) => r.starts_at);
        const { availableTokens } = GlobalStatesV3.currentUser;
        const isHomeLocationSet = GlobalStatesV3.currentUser.homeLocation.isHomeLocationSet;
        const homeLocationId = GlobalStatesV3.currentUser.homeLocation.locationId;

        return (
          <div id="session-pane" styleName="pane">
            {GlobalStatesV3.viewport.isMobile && <BackButton />}
            <div styleName="pane-header">
              <div styleName="pane-title">もくもく会の予約</div>
              <div styleName="pane-header-border" />
              <SessionWaitlistDescription />
            </div>
            <div styleName="views">
              <div styleName="calendar-wrapper">
                <CalendarView
                  startDate={moment().date(1)}
                  endDate={moment().add(6, 'month').date(1)}
                  selectedMultipleDates={selectedMultipleDates}
                  clickables={this.filterSessionsByKey(sessionsOfType, membershipPlanGroupKey).map((r) => r.starts_at)}
                  onClick={setSelectedDate}
                  onMonthChange={setSelectedMonth}
                />
              </div>
              <div styleName="schedule-list-wrapper">
                <div styleName="mokumoku-guide-banner">
                  <a
                    href="https://shelikes.jp/home?redirect_url=https://sheinc.notion.site/SHElikes-f50c771a10da4d49899f78f801b4fad7"
                    target="_blank"
                  >
                    <img src={MokumokuGuideBanner} styleName="mokumoku-guide-banner-img" />
                  </a>
                </div>
                <div styleName="schedule-list-header">
                  <div styleName="schedule-list-header-box">
                    <div styleName="schedule-list-header-text">表示する拠点</div>
                    <LocationSelector
                      locations={locations.getOrElse([])}
                      homeLocationId={homeLocationId}
                      isHomeLocationSet={isHomeLocationSet}
                      showOther={false}
                      showOnline={true}
                      showOnlineFirst={true}
                      locationSelectors={selectedLocationSelectors}
                      onChange={setSelectedLocationSelectors}
                    />
                  </div>
                  <div styleName="schedule-list-header-box">
                    <div styleName="schedule-list-header-text">開催場所</div>
                    <CheckBoxSelectInput
                      options={[
                        { label: 'Zoom', value: RoomType.ZOOM, valueForAttribute: RoomType.ZOOM },
                        { label: 'SHEstation', value: RoomType.SHE_STATION, valueForAttribute: RoomType.SHE_STATION },
                      ]}
                      color="white"
                      values={selectedRoomTypes}
                      className="room-type-filter"
                      onChange={setSelectedRoomTypes}
                    />
                  </div>
                  <div styleName="schedule-list-header-box">
                    <div styleName="schedule-list-header-text">空き状況</div>
                    <CheckBoxCardNoBorder
                      id="selectedOnlyOpen"
                      checked={selectedOnlyOpen}
                      label="空き枠のみ"
                      onCheck={(checked) => setSelectedOnlyOpen(checked)}
                    />
                  </div>
                  <div>
                    <div styleName="schedule-list-header-text">種類</div>
                    <SelectInput
                      options={[
                        {
                          value: NaN,
                          label: 'すべて',
                        },
                        ...sessionTemplates.getOrElse([]).map((s) => ({
                          value: s.id,
                          label: s.name,
                        })),
                      ]}
                      value={selectedSessionTemplateId}
                      serializer={(v) => String(v)}
                      deserializer={(v) => parseInt(v)}
                      onChange={(v) => setSelectedSessionTemplateId(v)}
                      styleName="select-input"
                    />
                  </div>
                </div>
                <div styleName="schedule-list-body">
                  {sessionListLoading && <LoaderRow />}
                  {groups.map((group) => (
                    <DateGroup key={group.date} date={group.date}>
                      {group.entries.map((entry) => {
                        const reserved = reservationList.isReserved(ReservableType.SESSION, entry.id);
                        const isReservable = availableTokens.isAffordable(
                          entry.required_token_type,
                          entry.required_token_amount,
                          entry.starts_at
                        );
                        return (
                          <ScheduleListItem
                            key={entry.id}
                            title={entry.template_name}
                            reservable={entry}
                            actionItem={
                              <div styleName="actions">
                                <Button
                                  id="session-reserve-button"
                                  styleName="button"
                                  type="primary"
                                  disabled={!isReservable || reserved || isFullSession(entry)}
                                  size="xs"
                                  // マイページリニューアルの移行用。ホームページ or もくもくページで遷移先を変更する
                                  // TODO: マイページリニューアルがリリースされたらお掃除する
                                  onClick={() =>
                                    GlobalStatesV3.routing.push(V3Routes.mokumokuSessionReservation(entry.id))
                                  }
                                >
                                  {reserved ? '予約済' : isFullSession(entry) ? '満席' : '選択する'}
                                </Button>
                                {isReservable && (
                                  <WaitlistToggleButton
                                    entry={entry}
                                    reserved={reserved}
                                    sessionWaitlistState={this.sessionWaitlistState}
                                  />
                                )}
                              </div>
                            }
                          />
                        );
                      })}
                    </DateGroup>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      }
    }
  )
);
